import {
  Background,
  BackgroundVariant,
  ConnectionLineType,
  ReactFlow,
  ReactFlowProvider,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import './style.scss'
import { GenericContextMenu } from '@neo/blocks/context-menu'
import { cn } from '@neo/lib/utils'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useLocation } from 'router-v6'
import { REACT_FLOW_MAX_ZOOM, REACT_FLOW_MIN_ZOOM } from 'v2/configs/constants'
import { useAppNavigate } from 'v2/shared/hooks/use-app-navigate'
import { useLivekitEnabled } from 'v2/shared/hooks/use-livekit-enabled'
import type { PhloType } from 'v2/types'
import { warningToast } from 'v2/utils/toasts'
import { AgentCanvasLoading } from './components/agent-builder-loading'
import { CallTriggerPromptDialog } from './components/call-trigger-prompt-dialog'
import { DuplicateStartNodeModal } from './components/duplicate-start-node-modal'
import { EvalSidebar } from './components/eval'
import { useSimulationStore } from './components/eval/simulation-store'
import { useEvalStore } from './components/eval/store'
import { FlowGradientDefinitions } from './components/flow-gradient-definitions'
import { LegacyFlowMigrationBanner } from './components/legacy-flow-migration-banner'
import { FlowVersionViewingBanner } from './components/title-bar/flow-version-viewing-banner'
import { TriggerChangeConfirmationModal } from './components/trigger-change-confirmation-modal'
import { TriggerDialog } from './components/trigger-dialog'
import { UnsavedChangesModal } from './components/unsaved-changes-modal'
import { useVibeSend } from './components/vibe-agent/use-vibe-send'
import { defaultEdge, edgeTypes } from './config/base.config'
import { nodeTypes } from './config/nodes.config'
import { FlowControls } from './controls'
import { useCopyPaste } from './hooks/use-copy-paste'
import { useFlowId } from './hooks/use-flow-id'
import { useFlowProps } from './hooks/use-flow-props'
import { useNavigationBlocking } from './hooks/use-navigation-blocking'
import { useTypedReactFlow } from './hooks/use-react-flow'
import { useVersionView } from './hooks/use-version-view'
import { Candidate } from './nodes/candidate'
import { SystemPromptNode } from './nodes/system-prompt-node'
import { MenuItems } from './panel'
import { RunInfo } from './run-info'
import { SideBar } from './sidebar'
import { useFlowStore } from './store'
import { AgentState, FlowNodeTypes } from './types'
import { hasAiAgentV2WithMeta, hasCallChannelAiAgentV2 } from './utils/call-channel-detection'

export const FlowCanvasContent = () => {
  const resetState = useFlowStore((state) => state.reset)
  const setFlowInstance = useFlowStore((state) => state.setFlowInstance)
  const showNodeSelectionModal = useFlowStore((state) => state.showNodeSelectionModal)
  const setShowNodeSelectionModal = useFlowStore((state) => state.setShowNodeSelectionModal)
  const setCurrentFlowType = useFlowStore((state) => state.setCurrentFlowType)
  const setShowVibeSidebar = useFlowStore((state) => state.setShowVibeSidebar)
  const showVibeSidebar = useFlowStore((state) => state.showVibeSidebar)
  const resetEvalState = useEvalStore((state) => state.reset)
  const clearSimulation = useSimulationStore((s) => s.clear)

  const flowInstance = useTypedReactFlow()
  const location = useLocation()
  const navigate = useAppNavigate()
  const sendVibeMessage = useVibeSend()
  const { isNew } = useFlowId()
  const { queryData, flowData, menuItems, undo, redo, ...reactFlowProps } = useFlowProps()
  const { isDuplicateStartNodeModalOpen, duplicateStartNodeModalActions } = useCopyPaste()
  const isSelectionModeActive = useFlowStore((state) => state.isSelectionModeActive)

  const setIsEditing = useFlowStore((state) => state.setIsEditing)
  const unselectNode = useFlowStore((state) => state.unselectNode)
  const { isViewingHistorical, viewingVersionId, viewVersion } = useVersionView()
  const { isLivekitEnabled } = useLivekitEnabled()
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally capture only on mount
  const arrivedViaMigration = useMemo(() => !!location.state?.initialPrompt, [])

  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    resetState()
    setFlowInstance(flowInstance)
    if (flowData?.phlo?.phlo_type) {
      setCurrentFlowType(flowData.phlo.phlo_type as PhloType)
    }
    return () => {
      resetState()
      resetEvalState()
      clearSimulation()
    }
  }, [])

  // Canvas tab is active when path has no tab suffix
  const isCanvasTab =
    !/\/(runs|knowledge-base|settings|secrets|external-tools|metrics|simulation)$/.test(
      location.pathname,
    )

  // fitView only when container is visible
  const applyFitView = useCallback(() => {
    const rect = containerRef.current?.getBoundingClientRect()
    if (!rect?.width || !rect?.height) return
    if (!flowData?.nodes?.length) return

    flowInstance?.fitView({
      nodes: flowData.nodes.map((n) => ({ id: n.id })),
      maxZoom: flowData.nodes.length > 7 ? 0.8 : 1,
      minZoom: REACT_FLOW_MIN_ZOOM,
      duration: 300,
      padding: 0.2,
    })
  }, [flowData, flowInstance])

  // Refit when canvas tab becomes active or Vibe sidebar opens/closes (which resizes the canvas).
  // 300ms accounts for the sidebar's 200ms width transition plus tab render delay.
  useEffect(() => {
    if (!isCanvasTab) return
    const id = setTimeout(() => requestAnimationFrame(applyFitView), 300)
    return () => clearTimeout(id)
  }, [isCanvasTab, applyFitView, showVibeSidebar])

  useEffect(() => {
    // Viewing a historical version (URL `?version`) is always read-only, whatever
    // the flow's own state — so a draft's old versions can't be edited in place.
    if (isViewingHistorical) {
      setIsEditing(false)
    } else if (!!flowData && hasAiAgentV2WithMeta(flowData.nodes)) {
      // TEMPORARY WORKAROUND (v3 edit-lock; remove when the task compiler is re-enabled):
      // v3 task-runtime flows (any ai_agent_v2 node with a compiled `_meta`) are frozen:
      // force read-only so all canvas + sidebar inputs (gated on isEditing) stay disabled.
      setIsEditing(false)
    } else if (flowData?.phlo?.state === AgentState.DRAFT) {
      setIsEditing(true)
    } else if (flowData?.phlo?.state) {
      setIsEditing(false)
    }
    if (flowData?.phlo?.phlo_type) setCurrentFlowType(flowData.phlo.phlo_type as PhloType)
  }, [flowData, isViewingHistorical])

  // Switching versions reloads the canvas with fresh node uuids; clear any open node
  // selection so the SideBar doesn't keep showing a node from the prior version. Pass
  // `true` to clear unconditionally — never pop the unsaved-changes modal here (that's
  // the dropdown's job before it changes the version). (SER-5591)
  // biome-ignore lint/correctness/useExhaustiveDependencies: clear only when the viewed version changes
  useEffect(() => {
    unselectNode(true)
  }, [viewingVersionId])

  // A stale/deleted `?version=<id>` (numeric but no longer a real version) 404s →
  // useFlowQuery resolves to null (queries.ts) → the page would otherwise spin forever
  // (the loader below gates on flowData === null). Self-heal back to the live version
  // and tell the user, rather than stranding them on a permanent loader. (SER-5591)
  useEffect(() => {
    if (isViewingHistorical && flowData === null) {
      viewVersion(null)
      warningToast({
        title: 'Version unavailable',
        description: 'That version no longer exists — showing the current version.',
      })
    }
  }, [isViewingHistorical, flowData, viewVersion])

  // Every new agent starts on a blank canvas, so Vibe Agent is the entry point: open the
  // Vibe Agent sidebar for any new flow, and for a prompt handed over from the homepage.
  useEffect(() => {
    const incomingPrompt = location.state?.initialPrompt

    if (incomingPrompt || isNew) {
      setShowVibeSidebar(true)
    }

    // The ONE consumer of the location.state prompt hand-off (homepage-era
    // new-flow starts + the Upgrade-to-v2 migration button). New flows send
    // against the live canvas; existing flows (migration) override with the
    // workflow built from flowData because the canvas may not be settled yet.
    if (incomingPrompt && (isNew || flowData)) {
      navigate(location.pathname + location.search, { replace: true })
      const workflowOverride =
        isNew || !flowData
          ? undefined
          : JSON.stringify({
              nodes: flowData.nodes.filter((n) => n.type !== FlowNodeTypes.select_trigger),
              edges: flowData.edges,
            })
      queueMicrotask(() => sendVibeMessage(incomingPrompt, { workflowOverride }))
    }
  }, [location.state, setShowVibeSidebar, isNew, flowData])

  // Handle flow navigation blocking
  const { isEditing } = useNavigationBlocking({ flowData: flowData ?? undefined })

  // Unsaved changes are now automatically detected by comparing flow state

  if (queryData.isPending || flowData === null) return <AgentCanvasLoading />

  const needsLivekitMigration =
    isLivekitEnabled &&
    !isNew &&
    !!flowData &&
    !flowData.global_meta?.livekit_ready &&
    hasCallChannelAiAgentV2(flowData.nodes)

  return (
    <div ref={containerRef} className="flow-builder h-full flex flex-col" data-testid="flow-base">
      <FlowGradientDefinitions />
      <GenericContextMenu className="h-full" menuItems={menuItems}>
        <div
          className={cn('relative h-full fade-in-0 animate-in duration-700', {
            'cursor-crosshair': isSelectionModeActive,
          })}
        >
          {needsLivekitMigration && !arrivedViaMigration && <LegacyFlowMigrationBanner />}
          <FlowVersionViewingBanner />
          <ReactFlow
            data-testid="react-flow"
            minZoom={REACT_FLOW_MIN_ZOOM}
            maxZoom={REACT_FLOW_MAX_ZOOM}
            connectionLineType={ConnectionLineType.SmoothStep}
            defaultEdgeOptions={defaultEdge}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            deleteKeyCode={isEditing ? ['Backspace', 'Delete'] : []}
            nodesDraggable={isEditing}
            nodesConnectable={isEditing}
            selectionOnDrag={isSelectionModeActive}
            panOnDrag={!isSelectionModeActive}
            onBeforeDelete={() => Promise.resolve(isEditing)}
            proOptions={{ hideAttribution: true }}
            {...reactFlowProps}
          >
            <Background
              variant={BackgroundVariant.Dots}
              gap={12}
              size={1}
              color="hsl(var(--muted-foreground) / 0.4)"
            />
            <FlowControls undo={undo} redo={redo} />
            <Candidate />
            <SystemPromptNode />
          </ReactFlow>
        </div>
      </GenericContextMenu>
      <SideBar />
      {/* VibeAgentSidebar is mounted in agent-builder.tsx so it stays visible across all tabs */}

      {isDuplicateStartNodeModalOpen && duplicateStartNodeModalActions && (
        <DuplicateStartNodeModal
          isOpen={isDuplicateStartNodeModalOpen}
          actions={duplicateStartNodeModalActions}
        />
      )}
      <RunInfo />
      <EvalSidebar />
      <UnsavedChangesModal />
      <TriggerDialog />
      <TriggerChangeConfirmationModal />
      <CallTriggerPromptDialog />
      <MenuItems
        open={showNodeSelectionModal}
        onOpenChange={setShowNodeSelectionModal}
        isStandalone={true}
        onSelect={() => {
          // This will be handled by the MenuItems component itself
          // since we're using it for standalone node creation
        }}
      />
    </div>
  )
}

const FlowBase = () => (
  <ReactFlowProvider>
    <FlowCanvasContent />
  </ReactFlowProvider>
)

export default FlowBase
